import React, { useState, useEffect } from 'react';
import './CostCalculator.css';

import { Heading } from '../../../components';

import { FaRulerCombined, FaHome, FaLayerGroup, FaBuilding, FaAngleDown, FaHardHat } from 'react-icons/fa';





const CostCalculator = () => {

  // Define constants
  const materialCosts = {
    concrete: 150,
    steel: 180,
    wood: 130,
    brick: 140
  };

  const defaultLaborCost = 50;

  const buildingTypeMultiplier = {
    residential: 1.0,
    commercial: 1.2,
    industrial: 1.5
  };

  const extraServicesCost = {
    plumbing: 10,
    electrical: 12,
    finishing: 8
  };

  // State for form inputs
  const [formData, setFormData] = useState({
    totalArea: '',
    buildingType: '',
    materialType: '',
    numFloors: '',
    laborCost: defaultLaborCost,
    plumbing: false,
    electrical: false,
    finishing: false
  });

  const [totalCost, setTotalCost] = useState(0);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Calculate cost whenever form data changes
  useEffect(() => {
    calculateCost();
  }, [formData]);

  const calculateCost = () => {
    const {
      totalArea,
      buildingType,
      materialType,
      numFloors,
      laborCost,
      plumbing,
      electrical,
      finishing
    } = formData;

    // Convert string inputs to numbers
    const area = parseFloat(totalArea) || 0;
    const floors = parseInt(numFloors) || 1;
    const labor = parseFloat(laborCost) || defaultLaborCost;

    // Check if required fields are filled
    if (!area || !buildingType || !materialType) {
      setTotalCost(0);
      return;
    }

    // Base construction cost
    let baseCost = area * materialCosts[materialType] * floors;

    // Apply building type multiplier
    baseCost *= buildingTypeMultiplier[buildingType];

    // Calculate labor cost
    let totalLaborCost = area * labor * floors;

    // Additional service costs
    let extraCost = 0;
    if (plumbing) extraCost += area * extraServicesCost.plumbing;
    if (electrical) extraCost += area * extraServicesCost.electrical;
    if (finishing) extraCost += area * extraServicesCost.finishing;

    // Calculate total cost
    const calculatedTotal = baseCost + totalLaborCost + extraCost;
    setTotalCost(calculatedTotal);
  };

  return (
    <section className="custom-form cost-calculator linear-bg">
      <div className="box-container">
        <div className="content">
          <form className="calculator-form dark-form">

            {/* Form Heading */}
            <Heading title={'Cost Calculator'} subTitle={"Estimate Your Construction Cost"} linear sideHeading />

            {/* Total Area Input Field */}
            <div className="input-box">
              <div className="input-field">
                <label htmlFor="total-area">
                  <FaRulerCombined />
                </label>
                <input
                  type="number"
                  name="totalArea"
                  className="box"
                  id="total-area"
                  min="0"
                  placeholder="Total Area (sq ft)"
                  value={formData.totalArea}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            {/* Building Type Select Field */}
            <div className="input-box">
              <div className="select">
                <div className="select-container">
                  <label htmlFor="building-type">
                    <FaHome />
                  </label>
                  <select
                    className="select-box"
                    id="building-type"
                    name="buildingType"
                    value={formData.buildingType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Building Type</option>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="industrial">Industrial</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown className="icon" />
                  </div>
                </div>
              </div>
            </div>

            {/* Construction Material Select Field */}
            <div className="input-box">
              <div className="select">
                <div className="select-container">
                  <label htmlFor="material-type">
                    <FaLayerGroup />
                  </label>
                  <select
                    className="select-box"
                    id="material-type"
                    name="materialType"
                    value={formData.materialType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Material</option>
                    <option value="concrete">Concrete</option>
                    <option value="steel">Steel</option>
                    <option value="wood">Wood</option>
                    <option value="brick">Brick</option>
                  </select>
                  <div className="icon-container">
                    <FaAngleDown className="icon" />
                  </div>
                </div>
              </div>
            </div>

            {/* Number of Floors Input Field */}
            <div className="input-box">
              <div className="input-field">
                <label htmlFor="num-floors">
                  <FaBuilding />
                </label>
                <input
                  type="number"
                  name="numFloors"
                  className="box"
                  id="num-floors"
                  min="1"
                  placeholder="Number of Stories"
                  value={formData.numFloors}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            {/* Labor Cost Input Field */}
            <div className="input-box">
              <div className="input-field">
                <label htmlFor="labor-cost">
                  <FaHardHat />
                </label>
                <input
                  type="number"
                  name="laborCost"
                  className="box"
                  id="labor-cost"
                  min="0"
                  placeholder={`Labor Cost (per sq ft, default: $${defaultLaborCost})`}
                  value={formData.laborCost}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>

            {/* Additional Services Checkboxes */}
            <div className="extra-services">
              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="plumbing-checkbox"
                  name="plumbing"
                  checked={formData.plumbing}
                  onChange={handleInputChange}
                />
                <label htmlFor="plumbing-checkbox">Plumbing</label>
                <div className="checkbox-indicator"></div>
              </div>

              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="electrical-checkbox"
                  name="electrical"
                  checked={formData.electrical}
                  onChange={handleInputChange}
                />
                <label htmlFor="electrical-checkbox">Electrical Work</label>
                <div className="checkbox-indicator"></div>
              </div>

              <div className="checkbox-label">
                <input
                  type="checkbox"
                  id="finishing-checkbox"
                  name="finishing"
                  checked={formData.finishing}
                  onChange={handleInputChange}
                />
                <label htmlFor="finishing-checkbox">Interior Finishing</label>
                <div className="checkbox-indicator"></div>
              </div>
            </div>

            {/* Total Cost Display */}
            <div className="cost-total">
              <h5>Total Cost</h5>
              <span>${totalCost.toLocaleString()}</span>
            </div>

          </form>
        </div>
      </div>
    </section>
  );
};

export default CostCalculator;